import React, { useState, useEffect } from 'react';
import SurveyForm from '../../../components/forms/SurveyForm';
import surveyJsonData from './vcx-fss-survey.json';
import './VCXFSSOnboarding.css';

/**
 * Organization Details Card Component
 */
const OrganizationCard = ({ orgData }) => {
  if (!orgData) return null;

  const { basic, addresses, taxonomies, number } = orgData;
  const locationAddress = addresses?.find(a => a.address_purpose === 'LOCATION') || addresses?.[0];

  return (
    <div className="card mt-4 org-card">
      <div className="card-body">
        <div className="d-flex align-items-center mb-3">
          <div className="org-icon me-3">
            🏥
          </div>
          <div>
            <h5 className="mb-1">{basic?.organization_name || 'Organization'}</h5>
            <p className="text-muted mb-0 small">NPI: {number || 'N/A'}</p>
          </div>
        </div>

        <div className="row g-3">
          {/* Specialty */}
          {taxonomies && taxonomies.length > 0 && (
            <div className="col-md-6 col-lg-4">
              <div className="card h-100">
                <div className="card-body">
                  <p className="text-uppercase text-muted small mb-1">Primary Specialty</p>
                  <p className="fw-semibold mb-1">{taxonomies[0].desc || 'N/A'}</p>
                  {taxonomies[0].code && (
                    <p className="text-muted small mb-0">Code: {taxonomies[0].code}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Address */}
          {locationAddress && (
            <div className="col-md-6 col-lg-4">
              <div className="card h-100">
                <div className="card-body">
                  <p className="text-uppercase text-muted small mb-1">Location Address</p>
                  <p className="fw-semibold mb-1">
                    {locationAddress.address_1}
                    {locationAddress.address_2 && <>, {locationAddress.address_2}</>}
                  </p>
                  <p className="text-muted small mb-0">
                    {locationAddress.city}, {locationAddress.state} {locationAddress.postal_code}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Contact */}
          {locationAddress?.telephone_number && (
            <div className="col-md-6 col-lg-4">
              <div className="card h-100">
                <div className="card-body">
                  <p className="text-uppercase text-muted small mb-1">Contact</p>
                  <p className="fw-semibold mb-1">
                    {locationAddress.telephone_number.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}
                  </p>
                  {locationAddress.fax_number && (
                    <p className="text-muted small mb-0">
                      Fax: {locationAddress.fax_number.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Status & Date */}
          <div className="col-md-6 col-lg-4">
            <div className="card h-100">
              <div className="card-body">
                <p className="text-uppercase text-muted small mb-1">Status & Enumeration</p>
                <p className="fw-semibold mb-1">
                  <span className={`status-indicator ${basic?.status === 'A' ? 'active' : 'inactive'}`}></span>
                  {basic?.status === 'A' ? 'Active' : 'Inactive'}
                </p>
                <p className="text-muted small mb-0">Since: {basic?.enumeration_date || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Authorized Official */}
        {basic?.authorized_official_first_name && (
          <div className="card mt-3">
            <div className="card-body">
              <p className="text-uppercase text-muted small mb-1">Authorized Official</p>
              <p className="fw-semibold mb-1">
                {basic.authorized_official_name_prefix && basic.authorized_official_name_prefix !== '--' ? basic.authorized_official_name_prefix + ' ' : ''}
                {basic.authorized_official_first_name} {basic.authorized_official_middle_name} {basic.authorized_official_last_name}
                {basic.authorized_official_credential ? ', ' + basic.authorized_official_credential : ''}
              </p>
              {basic.authorized_official_title_or_position && (
                <p className="text-muted small mb-0">{basic.authorized_official_title_or_position}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * VCX FSS Onboarding Page
 *
 * Full onboarding form for Ankr VCX FSS with autocomplete organization search
 */
const VCXFSSOnboarding = () => {
  const [selectedOrgData, setSelectedOrgData] = useState(null);

  // Listen for organization data updates from SurveyForm
  useEffect(() => {
    const handleOrgDataUpdate = (event) => {
      if (event.detail && event.detail.questionName === 'orgName') {
        setSelectedOrgData(event.detail.customData);
      }
    };

    window.addEventListener('surveyValueChanged', handleOrgDataUpdate);
    return () => window.removeEventListener('surveyValueChanged', handleOrgDataUpdate);
  }, []);

  const handleComplete = () => {
    alert('Survey completed! Check console for details.');
  };

  return (
    <div className="vcx-onboarding-page">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-10 col-xl-9">
            <SurveyForm
              json={surveyJsonData}
              onSubmit={handleComplete}
            />

            {/* Organization Details Card - shown after orgName selection */}
            {selectedOrgData && <OrganizationCard orgData={selectedOrgData} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VCXFSSOnboarding;
